// Generated by Claude Code
import { MeterDetailPanel } from '#components/domain/operations/meters/detail/meter-detail-panel.tsx'
import { MeterEditDrawer } from '#components/domain/operations/meters/edit/meter-edit-drawer.tsx'
import { ConfirmModal } from '#components/primitives/components/confirm-modal.tsx'
import { PermissionGuard } from '#components/tools/permission-guard.tsx'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { readAnyGroups } from '#core/entities/auth/roles/permission-gate-helpers.ts'
import MetersApi from '#core/api/operations/meters/Meters.ts'
import type Asset from '#core/entities/operations/assets/Asset.ts'
import { useUserState } from '#core/state/UserState.ts'
import { useDeleteConfirm } from '#hooks/use-delete-confirm.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_app/$companyId/meters/$meterId')({
  component: RouteComponent,
  staticData: {
    title: 'Meter Detail',
  },
})

function RouteComponent() {
  return (
    <PermissionGuard permissions={readAnyGroups(PermissionService.METERS)} redirectOnNotAuthorized>
      <MeterDetailPageContent />
    </PermissionGuard>
  )
}

function MeterDetailPageContent() {
  const { companyId, meterId } = Route.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation('meters')

  const [editMeter, setEditMeter] = useState<Asset | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const canDelete = useUserState().hasPermission(PermissionService.METERS, PermissionAction.DELETE)
  const deleteConfirm = useDeleteConfirm<Asset>()

  function handleBack() {
    void navigate({ to: '/$companyId/meters', params: { companyId } })
  }

  return (
    <div className="container-fluid d-flex flex-column h-100 py-3">
      <div className="row justify-content-center flex-grow-1">
        <div className="col-12 col-lg-10 col-xl-8 d-flex flex-column">
          <div className="mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
              onClick={handleBack}
            >
              <ArrowLeft size={14} />
              {t('shared.actions.backToList')}
            </button>
          </div>
          <div className="flex-grow-1 overflow-hidden card rounded-3 p-0">
            <MeterDetailPanel
              meterId={meterId}
              onClose={handleBack}
              onEdit={(meter) => setEditMeter(meter)}
              onDelete={canDelete ? (meter) => deleteConfirm.request(meter) : undefined}
              reloadKey={reloadKey}
            />
          </div>
        </div>
      </div>

      <MeterEditDrawer
        open={editMeter !== null}
        meter={editMeter}
        onClose={() => setEditMeter(null)}
        onSaved={() => { setEditMeter(null); setReloadKey((k) => k + 1) }}
      />

      {/* Same shared confirm the list page uses; the deleted meter has no detail left to show. */}
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        title={t('shared.actions.delete')}
        message={t('delete.confirm')}
        confirmLabel={t('shared.actions.delete')}
        cancelLabel={t('shared.actions.cancel')}
        genericErrorLabel={t('errors.deleteFailed')}
        onClose={deleteConfirm.cancel}
        onConfirm={async () => {
          const meter = deleteConfirm.pending
          if (!meter) return
          await new MetersApi().deleteDevice(meter.id)
          handleBack()
        }}
      />
    </div>
  )
}
